import React from 'react';
import { Button } from '@/components/ui/button';
import MapPin from '@/assets/icons/MapPin';
import ImageGallery from '@/components/ImageGallery';
import useDashboardLinkPrefix from '@/hooks/useDashboardLinkPrefix';
import { Link } from 'react-router-dom';
import {
  getMappedPropertyBuildingType,
  getPropertyAddress,
  getPropertyBoligmappaNumber,
  getPropertyBuildingId,
  getPropertyCustomTotalSpace,
  getPropertyMatrikkel,
  getPropertyUnitNumber,
} from '@/utils/properties';
import PropertySpacePopover from '@componentsV2/PropertyOverview/PropertySpacePopover';
import PrivateSharingIcon from '@/assets/icons/PrivateSharingIcon';
import PropertySharingDetails from '@componentsV2/PropertySharingDetails';
import { Skeleton } from '@/components/ui/skeleton';

const PropertyOverview = ({
  property,
  onPropertySpaceChange,
  setUpdatedProperty,
  fetching,
}) => {
  const dashboardLinkPrefix = useDashboardLinkPrefix();
  const editImagesLink = `${dashboardLinkPrefix}/images`;

  const unitNumber = getPropertyUnitNumber(property);
  const buildingNumber = getPropertyBuildingId(property);
  const boligmappaNumber = getPropertyBoligmappaNumber(property);

  const handlePropertySpaceUpdate = (updatedSpace) => {
    const updatedPropertyData = {
      ...property,
      customPropertyFacts: {
        ...property.customPropertyFacts,
        totalSpace: updatedSpace,
      },
    };
    setUpdatedProperty(updatedPropertyData);
    onPropertySpaceChange({ totalSpace: updatedSpace });
  };

  return (
    <div className="grid grid-cols-[1.5fr_1fr] gap-x-3 gap-y-8 relative">
      {fetching || !boligmappaNumber ? (
        <>
          <Skeleton className="bg-secondary h-[118px] md:h-[106px] md:w-[50%]" />
          <span className="hidden md:block" />
          <Skeleton className="bg-secondary rounded-lg h-[118px] md:h-[254px] w-full" />
          <Skeleton className="col-start-1 col-span-2 md:col-span-1 bg-secondary rounded-lg h-[254px] w-full" />
        </>
      ) : (
        <>
          <div className="flex flex-col items-start col-span-1 md:col-span-2">
            <Button
              asChild
              variant="link"
              className="text-[15px] md:text-[17px] p-0 text-foreground opacity-[50%] font-normal"
            >
              <Link to="/dashboard/overview">← Hjem</Link>
            </Button>
            <h1 className="text-[21px] md:text-[33px] font-medium md:font-bold">
              {getPropertyAddress(property)}
            </h1>
            <div className="flex items-center gap-1">
              <MapPin className="hidden md:block" />
              <p>
                {property.address.postalCode || ''}
                {(property.address.postalPlace &&
                  ` · ${property.address.postalPlace}`) ||
                  ''}
              </p>
            </div>
          </div>
          {/* <Button */}
          {/*   className="text-[17px] font-normal shadow-none justify-self-end mt-3 hidden md:flex" */}
          {/*   variant="outline" */}
          {/* > */}
          {/*   Endre detaljer */}
          {/* </Button> */}
          <div className="flex flex-col gap-4 items-start w-full col-start-1 col-span-2 md:col-span-1 py-[15px] md:py-4 px-[15px] md:px-5 bg-[#e6e6e645] border rounded-lg">
            <div className="grid grid-cols-2 gap-1 h-full w-full">
              <div>
                <p className="text-[14px] opacity-[50%]">Bruksenhet</p>
                <p className="text-[14px]">{unitNumber}</p>
              </div>
              <div>
                <p className="text-[14px] opacity-[50%]">Bygningsnummer</p>
                <p className="text-[14px]">{buildingNumber}</p>
              </div>
              <div>
                <p className="text-[14px] opacity-[50%]">Bygningstype</p>
                <p className="text-[14px] overflow-hidden text-ellipsis">
                  {getMappedPropertyBuildingType(property)}
                </p>
              </div>
              <div>
                <p className="text-[14px] opacity-[50%]">Størrelse</p>
                <p className="text-[14px]">
                  {getPropertyCustomTotalSpace(property)}m² (
                  <PropertySpacePopover
                    defaultValue={getPropertyCustomTotalSpace(property)}
                    onDataChange={handlePropertySpaceUpdate}
                  >
                    <Button
                      className="w-auto p-0 text-[#333] font-normal underline"
                      variant="link"
                    >
                      endre
                    </Button>
                  </PropertySpacePopover>
                  )
                </p>
              </div>
              <div>
                <p className="text-[14px] opacity-[50%]">Boligmappenummer</p>
                <p className="text-[14px]">{boligmappaNumber}</p>
              </div>
              <div>
                <p className="text-[14px] opacity-[50%]">Matrikkel</p>
                <p className="text-[14px]">{getPropertyMatrikkel(property)}</p>
              </div>
            </div>
            <div className="flex justify-between items-center w-full border-t gap-2 pt-4">
              <PropertySharingDetails
                property={property}
                dashboardLinkPrefix={dashboardLinkPrefix}
              />
              <div className="flex">
                <PrivateSharingIcon width={25} height={25} fill="#ccc" />
              </div>
            </div>
          </div>
          <div className="row-start-1 col-start-2 md:col-auto md:row-auto relative max-h-[100%] w-full object-cover rounded-lg overflow-hidden">
            <ImageGallery
              className="w-full"
              images={property.gallery || []}
              showPlaceholder
            />
            <Button
              className="absolute right-1 bottom-1 px-2 py-1 h-auto text-[12px]"
              variant="outline"
              asChild
            >
              <Link to={editImagesLink}>Rediger</Link>
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default PropertyOverview;
